import React, { useEffect, useState, useRef } from 'react';
import { Form } from '@unform/web';
import * as yup from 'yup';
import { format, parse, parseISO } from 'date-fns';
import ptBrLocale from 'date-fns/locale/pt-BR';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useSession } from '../../providers/SessionProvider';
import { getValidationSchema } from '../../utils/schemas';
import { REACT_APP_AWS_BUCKET_PATH } from '../../utils/config';
import api from '../../services/api';

import ContentHeader from '../../components/ContentHeader';
import Footer from '../../components/Footer';
import MainHeader from '../../components/MainHeader';
import SideMenu from '../../components/SideMenu';
import Input from '../../components/Input';
import MaskedInput from '../../components/MaskedInput';
import Textarea from '../../components/Textarea';
import MyLoadingOverlay from '../../components/MyLoadingOverlay';

function ProfilePage() {
  const inputFileRef = useRef(null);
  const formRef = useRef(null);
  const history = useHistory();
  const {
    setIsAssessoryMenuSelected,
    agent,
    setAgent,
    _token,
    isLoading,
    setIsLoading,
  } = useSession();
  const [profile_image, setProfileImage] = useState(null);
  const [dateOfBirth, _] = useState(() => {
    const birthDate = String(agent.date_of_birth).split('T')[0];

    return format(parseISO(birthDate), 'dd/MM/yyyy', {
      locale: ptBrLocale.ptBr,
    });
  });

  const handleKeyPress = event => {
    const keyCode = event.keyCode || event.which;
    const keyValue = String.fromCharCode(keyCode);
    if (/^[0-9\b]+$/.test(keyValue) === false) {
      event.preventDefault();
    }
  };

  const onFilechange = e => {
    const reader = new FileReader();
    const { files } = e.target;
    reader.onload = r => {
      setProfileImage(r.target.result);
    };
    reader.readAsDataURL(files[0]);
  };

  const onBtnClick = () => {
    inputFileRef.current.click();
  };

  const handleSubmit = async data => {
    try {
      // Remove all previous errors
      formRef.current.setErrors({});

      const agentDataSchema = getValidationSchema('agent');
      const schema = yup.object().shape(agentDataSchema);

      await schema.validate(data, {
        abortEarly: false,
      });

      const orderData = { ...data };

      orderData.cpf = String(orderData.cpf).replace(/[^0-9]+/g, '');
      orderData.cep = String(orderData.cep).replace(/[^0-9]+/g, '');
      orderData.phone = String(orderData.phone).replace(/[^0-9]+/g, '');
      orderData.cellphone = String(orderData.cellphone).replace(/[^0-9]+/g, '');
      orderData.date_of_birth = format(
        parse(orderData.date_of_birth, 'dd/MM/yyyy', new Date()),
        'yyyy-MM-dd',
      );

      if (!String(orderData.password).length) {
        delete orderData.password;
      }

      if (profile_image) {
        orderData.profile_image = profile_image;
      }

      const config = {
        headers: { Authorization: `Bearer ${_token}` },
      };

      setIsLoading(true);

      await api
        .put(`/api/agents/${agent.id}`, orderData, config)
        .then(response => {
          const { data } = response;
          setAgent(data);
          setIsLoading(false);

          toast.success('Dados atualizados com sucesso!', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        });
    } catch (err) {
      const validationErrors = {};

      setIsLoading(false);

      if (err instanceof yup.ValidationError) {
        err.inner.forEach(error => {
          validationErrors[error.path] = error.message;
        });

        formRef.current.setErrors(validationErrors);
      } else if (err.response) {
        const { message } = err.response.data;

        toast.error(`${message}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        toast.error(`${err}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    }
  };

  useEffect(() => {
    setIsAssessoryMenuSelected(false);
  }, []);

  return (
    <div className="interna gray">
      <div className="white" />
      <div className="container bug-height">
        <div className="row first-row">
          <div className="col grid_3 left-menu">
            <MainHeader />
            <SideMenu />
          </div>
          <div className="col grid_9 content">
            <ContentHeader />
            <h3>Minha Página - Perfil</h3>
            <div className="profile">
              {String(agent.monocard_user).length > 0 && (
                <div className="view">
                  <i>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="50"
                      height="50"
                      viewBox="0 0 50 50"
                    >
                      <g
                        id="Group_3390"
                        data-name="Group 3390"
                        transform="translate(-707 -354)"
                      >
                        <circle
                          id="Ellipse_50"
                          data-name="Ellipse 50"
                          cx="25"
                          cy="25"
                          r="25"
                          transform="translate(707 354)"
                          fill="#005b88"
                        />
                        <g id="user" transform="translate(720 365)">
                          <path
                            id="Path_2950"
                            data-name="Path 2950"
                            d="M93.887,13.711A6.634,6.634,0,0,0,98.735,11.7a6.633,6.633,0,0,0,2.008-4.847,6.634,6.634,0,0,0-2.009-4.847,6.854,6.854,0,0,0-9.695,0,6.634,6.634,0,0,0-2.009,4.847A6.633,6.633,0,0,0,89.04,11.7,6.635,6.635,0,0,0,93.887,13.711Zm0,0"
                            transform="translate(-82.193)"
                            fill="#fff"
                          />
                          <path
                            id="Path_2951"
                            data-name="Path 2951"
                            d="M23.69,255.455a16.933,16.933,0,0,0-.231-1.8,14.17,14.17,0,0,0-.442-1.808,8.931,8.931,0,0,0-.743-1.686,6.364,6.364,0,0,0-1.121-1.461,4.942,4.942,0,0,0-1.61-1.012,5.565,5.565,0,0,0-2.056-.372,2.087,2.087,0,0,0-1.114.473c-.334.218-.725.47-1.161.748a6.65,6.65,0,0,1-1.5.662,5.832,5.832,0,0,1-3.674,0,6.635,6.635,0,0,1-1.5-.661c-.432-.276-.823-.528-1.162-.749a2.084,2.084,0,0,0-1.114-.473,5.558,5.558,0,0,0-2.055.372,4.938,4.938,0,0,0-1.61,1.012,6.364,6.364,0,0,0-1.121,1.461,8.948,8.948,0,0,0-.743,1.687,14.2,14.2,0,0,0-.442,1.808,16.874,16.874,0,0,0-.231,1.8C.019,256,0,256.565,0,257.136a4.727,4.727,0,0,0,1.4,3.576,5.053,5.053,0,0,0,3.617,1.319H18.727a5.052,5.052,0,0,0,3.617-1.319,4.724,4.724,0,0,0,1.4-3.576C23.748,256.562,23.728,256,23.69,255.455Zm0,0"
                            transform="translate(0 -233.567)"
                            fill="#fff"
                          />
                        </g>
                      </g>
                    </svg>
                  </i>
                  <span>
                    Para visualizar sua página,{' '}
                    <a
                      href={`https://monocard.com.br/u/${agent.monocard_user}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      clique aqui.
                    </a>
                  </span>
                </div>
              )}

              <h4>
                Preencha seus dados abaixo e mantenha sua página atualizada:
              </h4>
              <Form
                onSubmit={data => handleSubmit(data)}
                ref={formRef}
                className="active"
              >
                <div className="foto">
                  <i>
                    {!profile_image && (
                      <img
                        src={
                          agent.profile_image
                            ? `${REACT_APP_AWS_BUCKET_PATH}/uploads/agents/images/${agent.profile_image}`
                            : `https://via.placeholder.com/141x137.png`
                        }
                        width="141"
                        height="137"
                        alt={agent.name}
                      />
                    )}

                    {profile_image && (
                      <img
                        src={profile_image}
                        width="141"
                        height="137"
                        alt={agent.name}
                      />
                    )}
                  </i>
                  <input
                    type="file"
                    accept="images/*"
                    ref={inputFileRef}
                    onChange={onFilechange}
                    name="image"
                    style={{ display: 'none' }}
                  />
                  <button onClick={e => onBtnClick()} type="button">
                    Alterar imagem
                  </button>
                </div>
                <h5>
                  <span>Dados principais:</span>
                </h5>
                <p>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Nome"
                    defaultValue={agent.name}
                  />
                </p>
                <p>
                  <Input
                    type="email"
                    name="email"
                    placeholder="E-mail"
                    defaultValue={agent.email}
                  />
                </p>
                <p>
                  <MaskedInput
                    type="text"
                    name="cpf"
                    placeholder="Nome"
                    defaultValue={agent.cpf}
                    mask="999.999.999-99"
                  />
                </p>
                <p>
                  <Input
                    type="text"
                    name="rg"
                    placeholder="RG"
                    onKeyPress={e => handleKeyPress(e)}
                    defaultValue={agent.rg}
                  />
                </p>
                <p>
                  <MaskedInput
                    type="text"
                    name="date_of_birth"
                    placeholder="Data de Nascimento"
                    defaultValue={dateOfBirth}
                    mask="99/99/9999"
                  />
                </p>
                <p>
                  <MaskedInput
                    type="text"
                    name="phone"
                    placeholder="Telefone"
                    defaultValue={agent.phone}
                    mask="(99)9999-9999"
                  />
                </p>
                <p>
                  <MaskedInput
                    type="text"
                    name="cellphone"
                    placeholder="Celular"
                    defaultValue={agent.cellphone}
                    mask="(99)99999-9999"
                  />
                </p>
                <p>
                  <MaskedInput
                    type="text"
                    name="cep"
                    placeholder="CEP"
                    defaultValue={agent.cep}
                    mask="99999-999"
                  />
                </p>
                <p>
                  <Input
                    type="text"
                    name="address"
                    placeholder="Endereço"
                    defaultValue={agent.address}
                  />
                </p>
                <p>
                  <Input
                    type="text"
                    name="city"
                    placeholder="Cidade"
                    defaultValue={agent.city}
                  />
                </p>
                <p>
                  <Input
                    type="text"
                    name="state"
                    maxLength="2"
                    placeholder="Estado"
                    defaultValue={agent.state}
                  />
                </p>
                <h5>
                  <span>Descrição:</span>
                </h5>
                <p>
                  <Textarea
                    name="description"
                    rows="4"
                    defaultValue={agent.description}
                    placeholder="Descrição"
                  />
                </p>
                <h5>
                  <span>Redes sociais:</span>
                </h5>
                <p>
                  <Input
                    type="text"
                    name="website"
                    placeholder="Website"
                    defaultValue={agent.website}
                  />
                </p>
                <p>
                  <Input
                    type="text"
                    name="facebook"
                    placeholder="Facebook"
                    defaultValue={agent.facebook}
                  />
                </p>
                <p>
                  <Input
                    type="text"
                    name="instagram"
                    placeholder="Instagram"
                    defaultValue={agent.instagram}
                  />
                </p>
                <p>
                  <Input
                    type="text"
                    name="youtube"
                    placeholder="Youtube"
                    defaultValue={agent.youtube}
                  />
                </p>
                <h5>
                  <span>Alteração de Senha:</span>
                </h5>
                <p>
                  <Input type="password" name="password" placeholder="Senha" />
                </p>
                <button
                  type="submit"
                  style={{ marginLeft: 0, marginTop: 30 }}
                  className="save"
                >
                  Salvar
                </button>
              </Form>
              {/* <form className="active">
                <h5>
                  <span>Fotos:</span> <button type="button">Editar</button>
                  <button type="button" className="save">
                    Salvar
                  </button>
                </h5>
                <ul>
                  <li className="add">
                    <a href="/" className="plus">
                      <i>+</i> Nova Imagem
                    </a>
                  </li>
                  <li>
                    <img
                      src="images/foto-modelo.jpg"
                      width="178"
                      height="178"
                      alt=""
                    />
                    <a href="/" className="del">
                      <i>X</i>
                    </a>
                  </li>
                  <li>
                    <img
                      src="images/foto-modelo.jpg"
                      width="178"
                      height="178"
                      alt=""
                    />
                    <a href="/" className="del">
                      <i>X</i>
                    </a>
                  </li>
                  <li>
                    <img
                      src="images/foto-modelo.jpg"
                      width="178"
                      height="178"
                      alt=""
                    />
                    <a href="/" className="del">
                      <i>X</i>
                    </a>
                  </li>
                  <li>
                    <img
                      src="images/foto-modelo.jpg"
                      width="178"
                      height="178"
                      alt=""
                    />
                    <a href="/" className="del">
                      <i>X</i>
                    </a>
                  </li>
                  <li>
                    <img
                      src="images/foto-modelo.jpg"
                      width="178"
                      height="178"
                      alt=""
                    />
                    <a href="/" className="del">
                      <i>X</i>
                    </a>
                  </li>
                  <li>
                    <img
                      src="images/foto-modelo.jpg"
                      width="178"
                      height="178"
                      alt=""
                    />
                    <a href="/" className="del">
                      <i>X</i>
                    </a>
                  </li>
                </ul>
              </form> */}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      {isLoading && <MyLoadingOverlay />}
    </div>
  );
}

export default ProfilePage;
